import api from './api'
import axios from 'axios'

export interface Exam {
  exam_id: number
  lesson_id?: number | null
  course_id?: number | null
  exam_name: string
  exam_content: {
    questions: Question[]
  }
  answer_key: Record<string, any>
  passing_rate: number
  total_attempts?: number | null
  time_limit?: number | null
  exam_status: number // 0 - inactive, 1 - active
  is_final_quiz: boolean
  created_at: string
  updated_at: string
  lesson?: {
    lesson_id: number
    name: string
    status: number
    description?: string
  }
}

export interface Question {
  id: number
  question: string
  type: 'multiple_choice' | 'true_false' | 'essay'
  options?: Array<{
    id: string
    text: string
  }>
}

export interface CreateExamPayload {
  exam_name: string
  exam_content: {
    questions: Question[]
  }
  answer_key: Record<string, any>
  passing_rate: number
  total_attempts?: number | null
  exam_status?: number
}

export interface UpdateExamPayload {
  exam_name?: string
  exam_content?: {
    questions: Question[]
  }
  answer_key?: Record<string, any>
  passing_rate?: number
  total_attempts?: number | null
  exam_status?: number
}

export interface ExamListResponse {
  success: boolean
  message: string
  data: {
    current_page: number
    data: Exam[]
    total: number
    per_page: number
    last_page: number
  }
  timestamp: string
}

export interface ExamResponse {
  success: boolean
  message: string
  data: Exam
  timestamp: string
}

export interface LessonExamsResponse {
  success: boolean
  message: string
  data: {
    lesson: {
      lesson_id: number
      name: string
      description?: string
      status: number
    }
    exams: Exam[]
  }
  timestamp: string
}

export const examService = {
  /**
   * Get all exams with optional filtering
   */
  async getExams(params?: {
    lesson_id?: number
    exam_status?: number
    sort_by?: string
    sort_order?: 'asc' | 'desc'
    per_page?: number
    page?: number
  }) {
    const queryParams = new URLSearchParams()
    
    if (params?.lesson_id) queryParams.append('lesson_id', params.lesson_id.toString())
    if (params?.exam_status !== undefined) queryParams.append('exam_status', params.exam_status.toString())
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by)
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order)
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString())
    if (params?.page) queryParams.append('page', params.page.toString())

    const url = `/exams${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return api.get<ExamListResponse>(url)
  },

  /**
   * Get all exams without pagination (for management interface)
   */
  async getAllExams() {
    return api.get<ExamListResponse>('/exams')
  },

  /**
   * Get a specific exam by ID
   */
  async getExam(examId: number) {
    return api.get<ExamResponse>(`/exams/${examId}`)
  },

  /**
   * Get all exams for a specific lesson
   */
  async getExamsByLesson(lessonId: number) {
    return api.get<LessonExamsResponse>(`/exams/lesson/${lessonId}`)
  },

  // /**
  //  * Create a new exam
  //  */
  // async createExam(payload: CreateExamPayload) {
  //   return api.post<ExamResponse>('/exams', payload)
  // },

  // /**
  //  * Update an existing exam
  //  */
  // async updateExam(examId: number, payload: UpdateExamPayload) {
  //   return api.put<ExamResponse>(`/exams/${examId}`, payload)
  // },

  /**
   * Update exam status (activate/deactivate)
   */
  async updateExamStatus(examId: number, status: number) {
    return api.put<ExamResponse>(`/exams/${examId}/status`, {
      exam_status: status
    })
  },

  /**
   * Delete an exam
   */
  async deleteExam(examId: number) {
    return api.delete(`/exams/${examId}`)
  },

  /**
 * Get exam results (employee or admin)
 */
  getExamResults(role: 'admin' | 'employee' = 'employee') {
    if (role === 'admin') {
      // Admin route – full access
      return axios.get('/api/exam-results')
    } else {
      // employee route – only their own exam results
      return axios.get('/api/exams/my-results')
    }
  },

  /**
   * Get a single exam result by ID (employee)
   * @param examId - The exam ID
   * @param resultId - The exam result ID
   */
  getMyExamResult(examId: number, resultId: number) {
    return axios.get(`/api/exams/${examId}/results/${resultId}`)
  }

}

export default examService
